/**
 * The keys derived from the link key (docs/FORMAT.md §1). The link key itself, as a CryptoKey, can only derive; nothing
 * seals with it. Derived keys are cached per link key, so a file's chunks and a bridge's messages do not derive again.
 */
import { FILE_KEY_INFO, FILE_NONCE_PATTERN, MESSAGE_KEY_INFO } from './labels';
import { DecryptError, deriveAesKey } from './primitives';

const encoder = new TextEncoder();

const messageKeys = new WeakMap<CryptoKey, Promise<CryptoKey>>();
const fileKeys = new WeakMap<CryptoKey, Map<string, Promise<CryptoKey>>>();

/** The key for the texts, file headers and deletion marks of a bridge. */
export function messageKey(linkKey: CryptoKey): Promise<CryptoKey> {
  let key = messageKeys.get(linkKey);
  if (key === undefined) {
    key = deriveAesKey(linkKey, encoder.encode(MESSAGE_KEY_INFO));
    messageKeys.set(linkKey, key);
  }
  return key;
}

/** The key for the chunks of one file: bound to the file's nonce, so no two files share a key. */
export function fileKey(linkKey: CryptoKey, nonce: string): Promise<CryptoKey> {
  if (!FILE_NONCE_PATTERN.test(nonce)) return Promise.reject(new DecryptError('Invalid file header.'));
  let byNonce = fileKeys.get(linkKey);
  if (byNonce === undefined) {
    byNonce = new Map();
    fileKeys.set(linkKey, byNonce);
  }
  let key = byNonce.get(nonce);
  if (key === undefined) {
    key = deriveAesKey(linkKey, new Uint8Array([...encoder.encode(FILE_KEY_INFO), 0, ...encoder.encode(nonce)]));
    byNonce.set(nonce, key);
  }
  return key;
}
